/**
 * CLI Prompt Library
 *
 * Browse and manage the Prompt Library from the terminal — curated,
 * agent-agnostic starter prompts. Lists Cofactor's GLOBAL prompts plus your
 * org's prompts; create/delete your org's prompts (and, as a superadmin,
 * GLOBAL ones with `--global`). Backed by the `/prompts` API.
 */

import fs from 'node:fs/promises';
import { writeRawOutput } from './brand.js';
import path from 'node:path';
import process from 'node:process';
import { resolveConfig, getArgValue, hasFlag } from './auth.js';
import { apiRequest } from './cli-helpers.js';

type PromptScope = 'global' | 'org';

interface PromptSummary {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  scope: PromptScope;
}

interface PromptDetail extends PromptSummary {
  content: string;
}

function printHelp(): void {
  const lines = [
    'Curated starter prompts for coding agents.',
    '',
    'Usage:',
    '  canary prompts list [--json]                       List global + your org prompts',
    '  canary prompts get <slug|id> [--json|--raw]        Print a prompt (markdown to stdout)',
    '  canary prompts create [<file>|--stdin] --title <t> [--description <d>] [--global] [--json]',
    '  canary prompts update <id> [<file>|--stdin|--content <t>] [--title <t>] [--description <d>] [--tags a,b] [--json]',
    '  canary prompts delete <id>                         Delete one of your org prompts',
    '',
    'Notes:',
    '  --global creates a Cofactor-wide prompt (superadmin only).',
    '  update patches only the fields you pass; --tags sets a GLOBAL prompt’s tags',
    '  (comma-separated; --tags "" clears them). Editing a global prompt is superadmin-only.',
    '',
    'Examples:',
    '  canary prompts list',
    '  canary prompts get install-cli',
    '  canary prompts create recipe.md --title "Set up CI"',
    '  cat recipe.md | canary prompts create --stdin --title "Set up CI"',
    '  cat recipe.md | canary prompts update 0eca867f-... --stdin --tags sandbox,workflows',
  ];
  console.log(lines.join('\n'));
}

async function readStdin(): Promise<string> {
  const chunks: Buffer[] = [];
  for await (const chunk of process.stdin) chunks.push(Buffer.from(chunk));
  return Buffer.concat(chunks).toString('utf-8');
}

async function promptsList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const result = await apiRequest<{ ok: boolean; error?: string; data: PromptSummary[] }>(
    apiUrl,
    token,
    'GET',
    '/prompts'
  );
  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'failed to list prompts'}`);
    process.exit(1);
  }
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(result.data, null, 2));
    return;
  }
  if (result.data.length === 0) {
    console.log('No prompts.');
    return;
  }
  const slugWidth = Math.max(...result.data.map((p) => p.slug.length));
  console.log('Available prompts:\n');
  for (const p of result.data) {
    const tag = p.scope === 'global' ? '[global]' : '[org]   ';
    console.log(`  ${tag}  ${p.slug.padEnd(slugWidth)}  ${p.title}`);
    if (p.description) console.log(`            ${' '.repeat(slugWidth)}  ${p.description}`);
  }
  console.log('\nPrint one with: canary prompts get <slug>');
}

async function promptsGet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const idOrSlug = argv.find((a) => !a.startsWith('-'));
  if (!idOrSlug) {
    console.error('Error: missing prompt. Usage: canary prompts get <slug|id> [--raw|--json]');
    process.exit(1);
  }
  const result = await apiRequest<{ ok: boolean; error?: string; data?: PromptDetail }>(
    apiUrl,
    token,
    'GET',
    `/prompts/${encodeURIComponent(idOrSlug)}`
  );
  if (!result.ok || !result.data) {
    console.error(`Error: ${result.error ?? 'prompt not found'}`);
    process.exit(1);
  }
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(result.data, null, 2));
    return;
  }
  // Raw markdown to stdout so it pastes cleanly into an agent.
  writeRawOutput(result.data.content);
}

async function promptsCreate(argv: string[], apiUrl: string, token: string): Promise<void> {
  // A positional file is only the FIRST arg (`prompts create <file> …`); anything
  // else is a flag (or a flag's value, e.g. `--title "Set up CI"`) and must not be
  // mistaken for a path.
  const filePath = argv[0] && !argv[0].startsWith('-') ? argv[0] : undefined;
  const inline = getArgValue(argv, '--content');
  let content: string;
  if (hasFlag(argv, '--stdin')) {
    content = await readStdin();
  } else if (inline !== undefined) {
    content = inline;
  } else if (filePath) {
    try {
      content = await fs.readFile(filePath, 'utf-8');
    } catch (err) {
      console.error(
        `Error: failed to read ${filePath}: ${err instanceof Error ? err.message : err}`
      );
      process.exit(1);
    }
  } else {
    console.error('Error: provide content via a <file> arg, --stdin, or --content <text>.');
    process.exit(1);
  }

  const title =
    getArgValue(argv, '--title') ??
    (filePath ? path.basename(filePath).replace(/\.[^.]+$/, '') : undefined);
  if (!title) {
    console.error('Error: a title is required. Pass --title <t>.');
    process.exit(1);
  }
  const description = getArgValue(argv, '--description');
  const scope = hasFlag(argv, '--global') ? 'global' : 'org';

  const result = await apiRequest<{ ok: boolean; error?: string; data?: PromptDetail }>(
    apiUrl,
    token,
    'POST',
    '/prompts',
    { title, content, scope, ...(description ? { description } : {}) }
  );
  if (!result.ok || !result.data) {
    console.error(`Error: ${result.error ?? 'failed to create prompt'}`);
    process.exit(1);
  }
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(result.data, null, 2));
    return;
  }
  console.log(`✓ Created ${result.data.scope} prompt "${result.data.title}"`);
  console.log(`  slug: ${result.data.slug}`);
  console.log(`  id:   ${result.data.id}`);
}

async function promptsUpdate(argv: string[], apiUrl: string, token: string): Promise<void> {
  // First positional is the prompt id; an optional second positional is a content
  // file. Flags — and the values of value-taking flags — are never positionals.
  const valueFlags = new Set(['--content', '--title', '--description', '--tags']);
  const positionals = argv.filter((a, i) => !a.startsWith('-') && !valueFlags.has(argv[i - 1] ?? ''));
  const id = positionals[0];
  if (!id) {
    console.error(
      'Error: missing prompt id. Usage: canary prompts update <id> [<file>|--stdin|--content <t>] [--title <t>] [--description <d>] [--tags a,b]'
    );
    process.exit(1);
  }

  // Content is optional — update only patches what you pass.
  const filePath = positionals[1];
  const inline = getArgValue(argv, '--content');
  let content: string | undefined;
  if (hasFlag(argv, '--stdin')) {
    content = await readStdin();
  } else if (inline !== undefined) {
    content = inline;
  } else if (filePath) {
    try {
      content = await fs.readFile(filePath, 'utf-8');
    } catch (err) {
      console.error(
        `Error: failed to read ${filePath}: ${err instanceof Error ? err.message : err}`
      );
      process.exit(1);
    }
  }

  const title = getArgValue(argv, '--title');
  const description = getArgValue(argv, '--description');
  const tagsArg = getArgValue(argv, '--tags');
  // `--tags ""` clears; a non-empty value is a comma-separated list.
  const globalTags =
    tagsArg !== undefined
      ? tagsArg
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
      : undefined;

  const body: Record<string, unknown> = {
    ...(content !== undefined ? { content } : {}),
    ...(title !== undefined ? { title } : {}),
    ...(description !== undefined ? { description } : {}),
    ...(globalTags !== undefined ? { globalTags } : {}),
  };
  if (Object.keys(body).length === 0) {
    console.error(
      'Error: nothing to update. Pass content (<file>/--stdin/--content), --title, --description, and/or --tags.'
    );
    process.exit(1);
  }

  const result = await apiRequest<{ ok: boolean; error?: string; data?: PromptDetail }>(
    apiUrl,
    token,
    'PUT',
    `/prompts/${encodeURIComponent(id)}`,
    body
  );
  if (!result.ok || !result.data) {
    console.error(`Error: ${result.error ?? 'failed to update prompt'}`);
    process.exit(1);
  }
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(result.data, null, 2));
    return;
  }
  console.log(`✓ Updated ${result.data.scope} prompt "${result.data.title}"`);
  console.log(`  slug: ${result.data.slug}`);
  console.log(`  id:   ${result.data.id}`);
}

async function promptsDelete(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv.find((a) => !a.startsWith('-'));
  if (!id) {
    console.error('Error: missing prompt id. Usage: canary prompts delete <id>');
    process.exit(1);
  }
  const result = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'DELETE',
    `/prompts/${encodeURIComponent(id)}`
  );
  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'failed to delete prompt'}`);
    process.exit(1);
  }
  console.log('✓ Deleted prompt');
}

export async function runPrompts(argv: string[]): Promise<void> {
  const [sub, ...rest] = argv;

  if (!sub || sub === 'help' || hasFlag(argv, '--help', '-h')) {
    printHelp();
    return;
  }

  const { apiUrl, token } = await resolveConfig(rest);

  switch (sub) {
    case 'list':
      return promptsList(rest, apiUrl, token);
    case 'get':
      return promptsGet(rest, apiUrl, token);
    case 'create':
      return promptsCreate(rest, apiUrl, token);
    case 'update':
      return promptsUpdate(rest, apiUrl, token);
    case 'delete':
      return promptsDelete(rest, apiUrl, token);
    default:
      console.error(`Unknown prompts subcommand: ${sub}`);
      printHelp();
      process.exit(1);
  }
}
